import { API, graphqlOperation } from 'aws-amplify'
import Lambda from 'aws-sdk/clients/lambda'
import { Auth } from 'aws-amplify'
import { capitalizeEachWord } from '../../../../lib/stringOperations'
import { getTrainingExpirationYears, isFromBuilding3WTC } from '../../../../utils/trainingExpirationUtils'
import { syncTrainingResultAcrossOccupancies } from '../../../../utils/trainingOccupancySyncUtils'

const updateOccupant = `mutation updateOccupant($pk: ID!, $sk: String!, $version: Int!,  $input: updateOccupantInput) {
    updateOccupant(pk: $pk, sk: $sk, expectedVersion: $version, input: $input) {
        pk
        sk
        d1
        d2
        firstName
        lastName
        email
        lastLogin
        version
        primaryContact
        registrationDate
        roles
        metaData {
            createdAt
        }
        phone {
            number
        }
        workPhone {
            number
            extension
        }
        extraFields {
            key
            value
        }
        version
    }
}`

const updateTraining = `
    mutation UpdateTrainingResult(
        $expectedVersion: Int!
        $input: updateTrainingResultInput
        $pk: ID!
        $sk: String!
    ) {
    updateTrainingResult(
        expectedVersion: $expectedVersion
        input: $input
        pk: $pk
        sk: $sk
    ) {
        certificateType
        completedAt
        createdAt
        d1
        d2
        expiredAt
        firstName
        lastName
        pk
        quizzesCompleted
        sk
        title {
            language
            text
        }
        updatedAt
        userType
        version
        videosCompleted
    }
}`

const deleteOccupant = `mutation deleteOccupant($pk: ID!, $sk: String) {
    deleteOccupant(pk: $pk, sk: $sk) {
        pk
        sk
        d1
        d2
        firstName
        lastName
        email
        lastLogin
        version
        primaryContact
        roles
        phone {
            number
        }
        workPhone {
            number
            extension
        }
        version
    }
}`

export const trimD1 = (str, n) => {
    const elements = str.split("#")
    const result = elements.slice(0, n).join("#")
    return result
}

export const archiveUser = async (pk, sk) => {
    try {
        const result = await API.graphql(graphqlOperation(deleteOccupant, { pk, sk }))
        // Dispatch event to notify other components that occupant data was updated
        window.dispatchEvent(new CustomEvent('occupant-data-updated'))
        return result;
    } catch (e) {
        console.log('error: ', e)
        throw Error(e)
    }
}

export const saveUser = async (pk, sk, version, data) => {

    const _data = {
        pk: pk,
        sk: sk,
        version: version || 1,
        input: data
    }

    try {
        const result = await API.graphql(graphqlOperation(updateOccupant, _data))
        // Dispatch event to notify other components that occupant data was updated
        window.dispatchEvent(new CustomEvent('occupant-data-updated'))
        return result
    } catch (e) {
        console.log('error: ', e)
        throw Error(e)
    }
}

export const createUser = async (pk, sk, version, data) => {

    const _data = {
        pk: pk,
        sk: sk,
        version: version || 1,
        input: data
    }

    try {
        const result = await API.graphql(graphqlOperation(updateOccupant, _data))
        // Dispatch event to notify other components that occupant data was updated
        window.dispatchEvent(new CustomEvent('occupant-data-updated'))
        return result
    } catch (e) {
        console.log('error: ', e)
        throw Error(e)
    }
}

export const resetPassword = async (email) => {
    return new Promise(function (resolve, reject) {
        Auth.currentCredentials().then((credentials) => {
            let lambda = new Lambda({
                region: 'us-west-2',
                apiVersion: '2015-03-31',
                credentials: Auth.essentialCredentials(credentials),
            })

            let payload = `{"username": "${email}"}`

            let params = {
                FunctionName: `arn:aws:lambda:us-west-2:${process.env.REACT_APP_AWS_ACCOUNT_ID}:function:BSS-AdminResetUserPassword-${process.env.REACT_APP_STAGE}-${process.env.REACT_APP_AWS_ACCOUNT_ID}`,
                InvocationType: 'RequestResponse',
                Payload: payload,
            }

            lambda.invoke(params, (err, data) => {
                if (err) {
                    reject(err)
                } else {
                    resolve(data)
                }
            })
        })
    })
}

export const resendInvite = async (email) => {
    return new Promise(function (resolve, reject) {
        Auth.currentCredentials().then((credentials) => {
            let lambda = new Lambda({
                region: 'us-west-2',
                apiVersion: '2015-03-31',
                credentials: Auth.essentialCredentials(credentials),
            })

            let payload = `{"email": "${email}"}`

            let params = {
                FunctionName: `arn:aws:lambda:us-west-2:${process.env.REACT_APP_AWS_ACCOUNT_ID}:function:BSS-ResendUserInvite-${process.env.REACT_APP_STAGE}-${process.env.REACT_APP_AWS_ACCOUNT_ID}`,
                InvocationType: 'RequestResponse',
                Payload: payload,
            }

            lambda.invoke(params, (err, data) => {
                if (err) {
                    reject(err)
                } else {
                    resolve(data)
                }
            })
        })
    })
}

export const markTrainingComplete = async (trainingResult, playlist, user, occupancies = []) => {

    let quizIds = []
    let videoIds = []

    playlist.sections.forEach((section) => {
        section.content.forEach((content) => {
            if (content.type == 'VIDEO')
                videoIds.push(content.id)
            if (content.type == 'QUIZ') {
                quizIds.push(content.id)
            }
        })
    })

    const _data = {
        pk: user.pk,
        sk: trainingResult.sk,
        expectedVersion: trainingResult.version || 1,
        input: {
            completedAt: Date.now(),
            expiredAt: null, // Clear expired timestamp when marking complete
            updatedAt: Date.now(),
            createdAt: trainingResult.createdAt || Date.now(),
            sk: `TRAINING_RESULT#${playlist.pk}#COMPLETE`,
            quizzesCompleted: quizIds,
            videosCompleted: videoIds,
            title: trainingResult.playlistTitle || playlist.meta.title,
            userType: trainingResult.userType || playlist.userType,
            certificateType: trainingResult.certificateType || playlist.certificateType,
            firstName: trainingResult.firstName || user.firstName,
            lastName: trainingResult.lastName || user.lastName,
            d2: trainingResult.d2 || user.d2,
            d1: trainingResult.d1 || user.d1.split("#").splice(0, 5).join("#")
        }
    }

    try {
        const result = await API.graphql(graphqlOperation(updateTraining, _data));
        const updatedResult = result.data.updateTrainingResult;

        if (occupancies && occupancies.length > 1) {
            try {
                await syncTrainingResultAcrossOccupancies(updatedResult, occupancies)
            } catch (syncError) {
                console.error('SYNC ERROR:', syncError)
            }
        }

        // Dispatch event to notify status change for the main result
        window.dispatchEvent(new CustomEvent('trainingStatusChange', {
            detail: {
                type: 'COMPLETE',
                userId: user.d2,
                playlistId: playlist.pk,
                trainingResult: updatedResult
            }
        }));
        return result;
    } catch (e) {
        console.log('error: ', e)
        throw Error(e)
    }
}

export const expireTrainingResults = async (trainingResult, occupancies = []) => {
    const _data = {
        pk: trainingResult.pk,
        sk: trainingResult.sk,
        expectedVersion: trainingResult.version,
        input: {
            // Keep completedAt to show when training was completed (if it was completed)
            // Keep videosCompleted and quizzesCompleted to preserve progress
            expiredAt: Date.now(), // Store when manually expired (current timestamp)
            sk: `TRAINING_RESULT#${trainingResult.sk.split('#')[1]}#EXPIRED`,
        }
    }

    try {
        const result = await API.graphql(graphqlOperation(updateTraining, _data));
        const updatedResult = result.data.updateTrainingResult;

        // Sync training result across user's other occupancies in the same building/tenant
        if (occupancies && occupancies.length > 1) {
            try {
                await syncTrainingResultAcrossOccupancies(updatedResult, occupancies)
            } catch (syncError) {
                console.error('Error syncing training results across occupancies:', syncError)
                // Don't fail the main operation if sync fails
            }
        }

        // Dispatch event to notify status change
        window.dispatchEvent(new CustomEvent('trainingStatusChange', {
            detail: {
                type: 'EXPIRED',
                userId: trainingResult.d2,
                playlistId: trainingResult.sk.split('#')[1]
            }
        }));
        return result;
    } catch (e) {
        console.log('error: ', e)
        throw Error(e)
    }
}

export const resetTrainingResult = async (result, occupancies = []) => {

    const _data = {
        expectedVersion: result.version,
        input: {
            completedAt: null,
            expiredAt: null, // Clear expired timestamp when resetting
            sk: `TRAINING_RESULT#${result.sk.split('#')[1]}#IN_PROGRESS`,
            quizzesCompleted: [],
            videosCompleted: [],
        },
        pk: result.pk,
        sk: result.sk
    }

    try {
        const response = await API.graphql(graphqlOperation(updateTraining, _data));
        const updatedResult = response.data.updateTrainingResult;

        // Sync training result across user's other occupancies in the same building/tenant
        if (occupancies && occupancies.length > 1) {
            try {
                await syncTrainingResultAcrossOccupancies(updatedResult, occupancies)
            } catch (syncError) {
                console.error('Error syncing training results across occupancies:', syncError)
                // Don't fail the main operation if sync fails
            }
        }

        // Dispatch event to notify status change
        window.dispatchEvent(new CustomEvent('trainingStatusChange', {
            detail: {
                type: 'IN_PROGRESS',
                userId: result.d2,
                playlistId: result.sk.split('#')[1]
            }
        }));
        return response;
    } catch (e) {
        console.log('error: ', e)
        throw Error(e)
    }
}

export const sendTrainingReminder = async (user, playlist) => {

    return new Promise(function (resolve, reject) {
        Auth.currentCredentials().then((credentials) => {
            let lambda = new Lambda({
                region: 'us-west-2',
                apiVersion: '2015-03-31',
                credentials: Auth.essentialCredentials(credentials),
            })

            let payload = `{"email": "${user.email}", "name": "${capitalizeEachWord(user.firstName)}", "playlist": "${playlist.playlistTitle[0].text}"}`

            let params = {
                FunctionName: `arn:aws:lambda:us-west-2:${process.env.REACT_APP_AWS_ACCOUNT_ID}:function:BSS-SendTrainingReminder-${process.env.REACT_APP_STAGE}-${process.env.REACT_APP_AWS_ACCOUNT_ID}`,
                InvocationType: 'RequestResponse',
                Payload: payload,
            }

            lambda.invoke(params, (err, data) => {
                if (err) {
                    reject(err)
                } else {
                    resolve(data)
                }
            })
        })
    })
}